import { Navigate, createBrowserRouter, RouterProvider } from "react-router-dom";

import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../store/userSlice";
import { checkEmptyObj, getUserRole } from "../utils";
import { RootState, store } from "../store";
import NotFound from "./NotFound";
import { MainSpinner, NavigationPlan } from "../components";
import Dashboard from "./Dashboard";
import MealPlanning from "./MealPlanning";
import Api from "../openapi";
import Meals from "./Meals";
import Users from "./Users";
import UserDetail from "./UserDetail";
import DeliveryReport from "./DeliveryReport";
import KitchenReport from "./KitchenReport";
import Plan from "./Plan";
import PlanDetail from "./PlanDetail";
import FinancialReport from "./FinancialReport";
import { Layout } from "./Layout";
import DeliveryFee from "./DeliveryFee";
import Review from "./Review";
import ChangePassword from "./ChangePassword";
import Discount from "./Discount";
import Drivers from "./Drivers";
import Areas from "./Areas";
import { getAccessToken, refreshAccessToken, cleanSession, migrateOldSession } from "../utils/tokenStore";

function Main() {
  const user = useSelector((state: RootState) => state.user);
  const generalSettings = useSelector((state: RootState) => state.generalSettings);
  const dispatch = useDispatch();

  const userRole = useMemo(() => {
    return getUserRole(user);
  }, [user]);

  const permissions = useMemo(() => {
    return user.permissions ? Array.from(user.permissions as Set<string>) : [];
  }, [user]);

  const router = useMemo(() => {
    return createBrowserRouter([
      {
        element: <Layout />,
        children: [
          {
            path: "/",
            loader: async () => {
              // Check if user already exists in Redux store
              const currentUser = store.getState().user;
              if (!checkEmptyObj(currentUser) && currentUser.email) {
                return currentUser;
              }

              // Try in-memory token, then migrate old cookie session, then refresh
              const token = getAccessToken() || await migrateOldSession() || await refreshAccessToken();
              if (!token) {
                return {};
              }

              try {
                const res = await Api.AdminUser.getUser();
                const user = res.data;
                dispatch(setUser(user));
                return user;
              } catch (error) {
                cleanSession();
                return {};
              }
            },
            children: [
              {
                index: true,
                element: <NavigationPlan />,
              },
              {
                path: "/dashboard",
                element: (
                  <ProtectedRoute
                    outlet={<Dashboard />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={true}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/meal-planning",
                element: (
                  <ProtectedRoute
                    outlet={<MealPlanning />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("MEAL_PLANNING_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/plan",
                element: (
                  <ProtectedRoute
                    outlet={<Plan />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("PLAN_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/plan/:id",
                element: (
                  <ProtectedRoute
                    outlet={<PlanDetail />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("PLAN_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/review",
                element: (
                  <ProtectedRoute
                    outlet={<Review />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("REVIEW_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/delivery/report",
                element: (
                  <ProtectedRoute
                    outlet={<DeliveryReport />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("DELIVERY_REPORT_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/delivery/fee",
                element: (
                  <ProtectedRoute
                    outlet={<DeliveryFee />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("DELIVERY_FEE_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/kitchen/report",
                element: (
                  <ProtectedRoute
                    outlet={<KitchenReport />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("KITCHEN_REPORT_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/financial/report",
                element: (
                  <ProtectedRoute
                    outlet={<FinancialReport />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("FINANCIAL_REPORT_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/meals",
                element: (
                  <ProtectedRoute
                    outlet={<Meals />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("MEAL_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/users",
                element: (
                  <ProtectedRoute
                    outlet={<Users />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("USER_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/users/:id",
                element: (
                  <ProtectedRoute
                    outlet={<UserDetail />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("USER_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/change-password",
                element: (
                  <ProtectedRoute
                    outlet={<ChangePassword />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={true}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/discount",
                element: (
                  <ProtectedRoute
                    outlet={<Discount />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("DISCOUNT_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/drivers",
                element: (
                  <ProtectedRoute
                    outlet={<Drivers />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    authorization={permissions.includes("DRIVER_VIEW")}
                    permissions={permissions}
                  />
                ),
              },
              {
                path: "/areas",
                element: (
                  <ProtectedRoute
                    outlet={<Areas />}
                    authenticationPath="/"
                    isAuthenticated={!checkEmptyObj(user)}
                    // authorization={permissions.includes("ZONE_VIEW")}
                    authorization={true}
                    permissions={permissions}
                  />
                ),
              },
              { path: "*", element: <NotFound /> },
            ],
          },
        ],
      },
    ]);
  }, [userRole]);

  return <RouterProvider router={router} fallbackElement={<MainSpinner />} />;
}

export default Main;

export type ProtectedRouteProps = {
  isAuthenticated: boolean;
  authenticationPath: string;
  outlet: JSX.Element;
  authorization?: boolean;
  permissions: string[];
};

export function ProtectedRoute({
  isAuthenticated,
  authenticationPath,
  outlet,
  authorization,
  permissions,
}: ProtectedRouteProps) {
  // authorization layer
  if (authorization === undefined) {
    return null;
  }

  if (isAuthenticated && authorization) {
    return outlet;
  } else {
    const params = new URLSearchParams(document.location.search);
    const invitationCode = params.get("code");
    // TODO: prevent loop if the authentication path "/" is not available
    return <Navigate to={{ pathname: authenticationPath }} />;
  }
}
